import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { BACKGROUND_TASK_KIND } from '@bower/shared/constants/background-task';
import {
  SPREADSHEET_CONVERT_MESSAGES,
  SPREADSHEET_INTAKE_NOTE_MESSAGES,
  classifySpreadsheetFile,
} from '@bower/shared/constants/spreadsheet-intake';
import type { SpreadsheetIntakeNote } from '@bower/shared';

import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { ROUTES } from '../../constants/routes';
import { destinationProblem } from '../../components/features/create/destination/createDestination';
import { apiService } from '../../services/api.service';
import { ApiResponseError } from '../../services/api/http-client.types';
import { createBlankSpreadsheet } from '../../services/api/spreadsheets.api';
import { performResumableUpload } from '../../services/upload/performUpload';
import { useEntityActivityStore } from '../../stores/entityActivityStore';
import { safeAnalytics } from '../../utils/analytics';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { useAppToast } from '../ui/useAppToast';

/** Where a new spreadsheet should land: a collection, or the workspace root. */
export interface SpreadsheetCreateScope {
  readonly projectId?: string;
}

function isIntakeNote(value: string | undefined): value is SpreadsheetIntakeNote {
  return value !== undefined && Object.hasOwn(SPREADSHEET_INTAKE_NOTE_MESSAGES, value);
}

/** Client-side mirror of `resolveSpreadsheetIntake`'s tabular checks (backend
 * `services/spreadsheet/intake/resolve-intake.ts`), run BEFORE a resumable
 * upload: the reserve door stores a refused file as a plain attachment
 * instead of refusing it outright, so refusing here means it is never
 * uploaded at all, matching what the multipart route does today. An
 * importable file comes back with its format. */
function preCheckSpreadsheetUpload(
  file: File,
): { readonly refusal: string } | { readonly format: 'csv' | 'xlsx' } {
  const fileClass = classifySpreadsheetFile(file.name, file.size);
  if (fileClass.kind === 'not-tabular') {
    return { refusal: SPREADSHEET_CONVERT_MESSAGES.NOT_IMPORTABLE };
  }
  if (fileClass.kind === 'refused') {
    return { refusal: SPREADSHEET_INTAKE_NOTE_MESSAGES[fileClass.note] };
  }
  return { format: fileClass.format };
}

interface UseCreateSpreadsheetOptions {
  /**
   * The collection refused the create (trashed, or edit access lost). Given,
   * it replaces the error toast for those failures; every other failure toasts.
   */
  readonly onDestinationProblem?: (problem: 'gone' | 'forbidden') => void;
}

/** A failed response as the shared API error, so its code survives. */
function toApiError(
  error: { message?: string; code?: string; status?: number } | undefined,
  fallbackMessage: string,
): ApiResponseError {
  return new ApiResponseError(error?.message || fallbackMessage, error?.code, error?.status);
}

/**
 * The two ways a spreadsheet begins, then opening it. Both land in the
 * collection named by `scope`, or at the workspace root.
 */
export function useCreateSpreadsheet({ onDestinationProblem }: UseCreateSpreadsheetOptions = {}) {
  const navigate = useNavigate();
  const toast = useAppToast();
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const [isBusy, setIsBusy] = useState(false);

  // A destination refusal goes to the caller when it asked for it; anything
  // else is the toast it always was, with the same message.
  const fail = useCallback(
    (title: string, error: Error) => {
      const problem = onDestinationProblem ? destinationProblem(error) : null;
      if (problem && onDestinationProblem) onDestinationProblem(problem);
      else toast({ title, description: error.message, status: 'error' });
    },
    [toast, onDestinationProblem],
  );

  const open = useCallback(
    (artifactId: string) => {
      void queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
      void queryClient.invalidateQueries({ queryKey: ['recents'] });
      navigate(ROUTES.spreadsheet(artifactId));
    },
    [queryClient, navigate],
  );

  const seedImportedAndOpen = useCallback(
    (artifactId: string, format: 'xlsx' | 'csv', scope: SpreadsheetCreateScope) => {
      // The bytes are stored and the import is queued: show it running on
      // every surface now, ahead of the first /sync event. seedTask, not
      // setTask: if the completion event already arrived, this stale
      // "queued" seed must not overwrite it.
      useEntityActivityStore.getState().seedTask(artifactId, {
        kind: BACKGROUND_TASK_KIND.SPREADSHEET_IMPORT,
        status: 'queued',
        slotKey: BACKGROUND_TASK_KIND.SPREADSHEET_IMPORT,
      });
      safeAnalytics.track(ANALYTICS_EVENTS.SPREADSHEET_IMPORTED, {
        format,
        in_collection: Boolean(scope.projectId),
        source: 'create',
      });
      open(artifactId);
    },
    [open],
  );

  const importFileResumable = useCallback(
    async (file: File, scope: SpreadsheetCreateScope): Promise<boolean> => {
      const preCheck = preCheckSpreadsheetUpload(file);
      if ('refusal' in preCheck) {
        toast({ title: 'Import failed', description: preCheck.refusal, status: 'error' });
        return false;
      }
      if (!workspaceId) {
        toast({
          title: 'Import failed',
          description: 'Please select a workspace first.',
          status: 'error',
        });
        return false;
      }
      let result;
      try {
        result = await performResumableUpload(file, {
          workspaceId,
          parentEntityType: scope.projectId ? 'project' : 'workspace',
          parentEntityId: scope.projectId ?? workspaceId,
          uploadIntent: 'import',
          // Revert-safety: a reverted backend strips the non-strict-zod
          // `uploadIntent` field and resolves `importAsNotes` instead. The
          // boolean rides alongside so a revert still imports rather than
          // silently falling back to attach-only; the current backend lets
          // `uploadIntent` win when both are present.
          importAsNotes: true,
          importSource: 'create',
          resumeAmbiguousFinalizeOnce: true,
        });
      } catch (error) {
        // The upload's own error class carries the backend code (a trashed
        // collection is `INVALID_PARENT` at reserve).
        const code = (error as { code?: unknown } | null)?.code;
        fail(
          'Import failed',
          toApiError(
            {
              message: error instanceof Error ? error.message : undefined,
              code: typeof code === 'string' ? code : undefined,
            },
            'This file could not be imported.',
          ),
        );
        return false;
      }
      if (result.artifactType !== 'spreadsheet') {
        // A race (e.g. the workspace turned PHI mid-upload): the backend
        // intake decision disagreed with this hook's pre-check. The file
        // still landed, as a plain attachment, so nothing was lost - this
        // only stops the spreadsheet UI from opening a file that isn't one.
        toast({
          title: 'Import failed',
          description: isIntakeNote(result.intakeNote)
            ? SPREADSHEET_INTAKE_NOTE_MESSAGES[result.intakeNote]
            : SPREADSHEET_CONVERT_MESSAGES.NOT_IMPORTABLE,
          status: 'error',
        });
        return false;
      }
      seedImportedAndOpen(result.artifactId, preCheck.format, scope);
      return true;
    },
    [toast, workspaceId, seedImportedAndOpen, fail],
  );

  const importFile = useCallback(
    async (file: File, scope: SpreadsheetCreateScope): Promise<boolean> => {
      setIsBusy(true);
      try {
        return await importFileResumable(file, scope);
      } finally {
        setIsBusy(false);
      }
    },
    [importFileResumable],
  );

  const createBlank = useCallback(
    async (scope: SpreadsheetCreateScope): Promise<boolean> => {
      setIsBusy(true);
      try {
        const response = await createBlankSpreadsheet(apiService, scope.projectId);
        if (!response.success || !response.data) {
          fail('Could not create spreadsheet', toApiError(response.error, 'Please try again.'));
          return false;
        }
        safeAnalytics.track(ANALYTICS_EVENTS.SPREADSHEET_CREATED, {
          in_collection: Boolean(scope.projectId),
        });
        open(response.data.artifactId);
        return true;
      } finally {
        setIsBusy(false);
      }
    },
    [fail, open],
  );

  return { importFile, createBlank, isBusy };
}
